/**
 * PostMessageBridge — Serviço centralizado de comunicação PostMessage
 * 
 * Gerencia TODA comunicação entre o Broadcast (iframe) e o app pai (Chatwoot/Konverte).
 * Padrão Singleton com pub/sub para handlers tipados.
 * 
 * @example
 * // Registrar handler para receber appContext
 * PostMessageBridge.on('appContext', (data) => {
 *   console.log('Dados recebidos:', data);
 * });
 * 
 * // Enviar mensagem ao pai
 * PostMessageBridge.send('broadcast:ready', { version: '2.0.2' });
 */

import type {
  PostMessageEnvelope,
  PostMessageHandler,
  AppContextPayload,
  IncomingEventMap,
} from '../types/postMessage';

// Origens permitidas — lê de window.__APP_CONFIG__ (runtime, injetado pelo nginx)
// Fallback para VITE_ALLOWED_ORIGINS (dev local com vite)
const getAllowedOrigins = (): string[] => {
  const appConfig = (window as any).__APP_CONFIG__;
  const envOrigins = appConfig?.allowedOrigins || import.meta.env.VITE_ALLOWED_ORIGINS;
  if (!envOrigins || envOrigins.trim() === '' || envOrigins === '${ALLOWED_ORIGINS}') {
    return ['*']; // Template não processado ou dev — aceita todas
  }
  return envOrigins.split(',').map((o: string) => o.trim()).filter(Boolean);
};

class PostMessageBridgeService {
  private handlers: Map<string, Set<PostMessageHandler>> = new Map();
  private allowedOrigins: string[];
  private isIframe: boolean;
  private initialized: boolean = false;
  private lastAppContext: AppContextPayload | null = null;

  constructor() {
    this.allowedOrigins = getAllowedOrigins();
    this.isIframe = typeof window !== 'undefined' && window !== window.parent;
  }

  // ============================================================================
  // INICIALIZAÇÃO
  // ============================================================================

  /**
   * Inicializa o bridge — deve ser chamado uma vez no carregamento do app
   */
  init(): void {
    if (this.initialized) return;
    this.initialized = true;

    // console.log('🌉 [PostMessageBridge] Inicializando...');
    // console.log('🌉 [PostMessageBridge] É iframe:', this.isIframe);
    // console.log('🌉 [PostMessageBridge] Origens permitidas:', this.allowedOrigins);

    // Registrar listener global
    window.addEventListener('message', this.handleMessage);

    // Se está dentro de iframe, solicitar dados e emitir ready
    if (this.isIframe) {
      // Aguardar um tick para garantir que handlers estão registrados
      setTimeout(() => {
        this.requestAppContext();
        this.send('broadcast:ready', { version: '2.0.2' });
      }, 300);
    }
  }

  /**
   * Destrói o bridge — remove listeners
   */
  destroy(): void {
    window.removeEventListener('message', this.handleMessage);
    this.handlers.clear();
    this.initialized = false;
    // console.log('🌉 [PostMessageBridge] Destruído');
  }

  // ============================================================================
  // RECEBIMENTO (Parent → Iframe)
  // ============================================================================

  /**
   * Handler global de mensagens recebidas via postMessage
   */
  private handleMessage = (event: MessageEvent): void => {
    // Validar origem (se não está em modo dev)
    if (!this.isOriginAllowed(event.origin)) {
      // console.warn('🌉 [PostMessageBridge] Origem bloqueada:', event.origin);
      return;
    }

    try {
      // Parse da mensagem (pode ser string JSON ou objeto)
      const message: PostMessageEnvelope = 
        typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

      // Ignorar mensagens sem evento
      if (!message || !message.event) return;

      // console.log('🌉 [PostMessageBridge] 📨 Recebido:', message.event, message.data);

      // Salvar último appContext
      if (message.event === 'appContext' && message.data) {
        this.lastAppContext = message.data as AppContextPayload;
      }

      // Notificar handlers registrados
      this.emit(message.event, message.data);

    } catch {
      // Ignorar mensagens que não são JSON válido (ex: mensagens do React DevTools)
    }
  };

  /**
   * Verifica se a origem da mensagem é permitida
   */
  private isOriginAllowed(origin: string): boolean {
    if (this.allowedOrigins.includes('*')) return true;
    return this.allowedOrigins.some(allowed => {
      if (allowed === origin) return true;
      try {
        const allowedUrl = new URL(allowed);
        const originUrl = new URL(origin);
        return allowedUrl.hostname === originUrl.hostname;
      } catch {
        return false;
      }
    });
  }

  // ============================================================================
  // ENVIO (Iframe → Parent)
  // ============================================================================

  /**
   * Envia mensagem para o app pai
   */
  send<K extends string>(event: K, data?: any): void {
    if (!this.isIframe) {
      // console.log('🌉 [PostMessageBridge] ⏭️ Não é iframe, envio ignorado:', event);
      return;
    }

    // Mensagens de fetch-info são enviadas como string simples (protocolo Chatwoot)
    if (event === 'chatwoot-dashboard-app:fetch-info') {
      // console.log('🌉 [PostMessageBridge] 📤 Solicitando appContext ao pai');
      window.parent.postMessage(event, '*');
      return;
    }

    // Demais mensagens seguem o envelope padrão
    const envelope: PostMessageEnvelope = { event, data };
    // console.log('🌉 [PostMessageBridge] 📤 Enviando:', event, data);
    window.parent.postMessage(JSON.stringify(envelope), '*');
  }

  /**
   * Solicita appContext ao pai (re-fetch)
   */
  requestAppContext(): void {
    this.send('chatwoot-dashboard-app:fetch-info');
  }

  // ============================================================================
  // PUB/SUB — Registro de Handlers
  // ============================================================================

  /**
   * Registra um handler para um evento específico
   * @returns Função de cleanup para remover o handler
   */
  on<K extends keyof IncomingEventMap>(
    event: K,
    handler: PostMessageHandler<IncomingEventMap[K]>
  ): () => void;
  on(event: string, handler: PostMessageHandler): () => void;
  on(event: string, handler: PostMessageHandler): () => void {
    if (!this.handlers.has(event)) {
      this.handlers.set(event, new Set());
    }
    this.handlers.get(event)!.add(handler);

    // Retorna função de cleanup
    return () => {
      this.handlers.get(event)?.delete(handler);
    };
  }

  /**
   * Remove um handler específico
   */
  off(event: string, handler: PostMessageHandler): void {
    this.handlers.get(event)?.delete(handler);
  }

  /**
   * Emite um evento para todos os handlers registrados
   */
  private emit(event: string, data: any): void {
    const eventHandlers = this.handlers.get(event);
    if (eventHandlers) {
      eventHandlers.forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error(`🌉 [PostMessageBridge] Erro no handler de "${event}":`, error);
        }
      });
    }
  }

  // ============================================================================
  // GETTERS
  // ============================================================================

  /** Retorna true se o app está dentro de um iframe */
  get isInsideIframe(): boolean {
    return this.isIframe;
  }

  /** Retorna o último appContext recebido (cache) */
  get cachedAppContext(): AppContextPayload | null {
    return this.lastAppContext;
  }
}

// Singleton export
export const PostMessageBridge = new PostMessageBridgeService();
